<template>
  <ChartLayout
    :title="`Operational and planned ${
      mode === 'status' ? 'capture ' : ''
    }capacity`"
    units="Mt CO2 per year"
    half
  >
    <template #params>
      <ButtonGroup
        small
        :options="[
          { label: 'By status', value: 'status' },
          {
            label: 'By project type',
            value: 'projectType',
          },
        ]"
        v-model="mode"
      />
    </template>
    <Chart :options="options" :key="mode" />
    <template #notes>
      <p v-if="mode === 'status'">
        Includes all operational and operational, under construction and planned
        CO2 capture facilities with an announced capacity of more than 100 000 t
        per year (or 1000 t per year for direct air capture facilities) and an
        announced timeline. Obtained by summing the estimated capacity of
        capture, full chain and CCU projects in the IEA CCUS Projects Database.
        2024e includes all announced capacity which could come online by the
        end of 2024.
      </p>
      <p v-else>
        Notes: Includes operational and operational, under construction and
        planned CO2 capture facilities and storage projects with an announced
        capacity of more than 100 000 t per year (or 1000 t per year for direct
        air capture facilities), an announced timeline, and excluding
        utilisation ('CCU') projects. Storage capacity is obtained by summing
        the estimated capacity of full chain, storage and transport & storage
        projects in the IEA CCUS Projects Database. 2024e includes all
        announced capacity which could come online by the end of 2024.
      </p>
    </template>
  </ChartLayout>
</template>

<script>
import { ButtonGroup } from "@international-energy-agency/vue-ui";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
import { Chart } from "highcharts-vue";
export default {
  components: {
    ButtonGroup,
    ChartLayout,
    Chart,
  },
  props: {
    projects: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      mode: "status",
      years: [2024, 2026, 2028, 2030],
    };
  },
  computed: {
    byStatus() {
      const { projects, years } = this;
      return projects.reduce(
        (byYear, project) => {
          if (["T&S", "Transport", "Storage"].includes(project.projectType))
            return byYear;
          let existing = byYear.find(
            (series) => series.name === project.status
          );
          if (!existing) return byYear;
          const yearIndex = years.findIndex((y) => y >= project.year);
          if (yearIndex < 0) return byYear;
          for (let i = yearIndex; i < years.length; i += 1) {
            existing.data[i] += project.capacity;
          }
          return byYear;
        },
        ["Operational", "Under construction", "Planned"].map((status) => ({
          name: status,
          data: [...years].fill(0),
        }))
      );
    },
    byYearAndType() {
      const { projects, years } = this;
      return projects.reduce(
        (byYearAndType, project) => {
          const yearIndex = years.findIndex((y) => y >= project.year);
          if (yearIndex < 0) return byYearAndType;
          if (project.projectType === "Full chain") {
            for (let i = yearIndex; i < years.length; i += 1) {
              byYearAndType[0].data[i] += project.capacity;
              byYearAndType[1].data[i] += project.capacity;
            }
            return byYearAndType;
          }
          let projectType = project.projectType;
          // Merge storage and T&S
          if (projectType === "T&S") projectType = "Storage";
          if (projectType === "Capture" || projectType === "Storage") {
            const existing = byYearAndType.find(
              (series) => series.name === projectType
            );
            for (let i = yearIndex; i < years.length; i += 1) {
              existing.data[i] += project.capacity;
            }
          }
          return byYearAndType;
        },
        [
          {
            name: "Full chain",
            id: "fullchain_capture",
            stack: "capture",
            colorIndex: 99,
            data: [...years].fill(0),
          },
          {
            name: "Full chain",
            id: "fullchain_storage",
            stack: "storage",
            colorIndex: 99,
            linkedTo: "fullchain_capture",
            data: [...years].fill(0),
          },
          {
            name: "Capture",
            id: "capture",
            stack: "capture",
            data: [...years].fill(0),
          },
          {
            name: "Storage",
            id: "storage",
            stack: "storage",
            data: [...years].fill(0),
          },
        ]
      );
    },
    options() {
      const { mode, byStatus, byYearAndType, years } = this;
      /***
       * The ChartOptions factory will produce a basic Highcharts options object with sensible default values.
       * Any options you pass in the first parameter are merged onto the defaults, overwriting them if necessary.
       * */

      return ChartOptions.buildOptions(
        {
          series: mode === "projectType" ? byYearAndType : byStatus,
          xAxis: {
            type: "category",
            categories: years.map((year) =>
              year === 2024 ? `${year}e` : year
            ),
          },
          tooltip: { valueSuffix: " Mt CO2 per year" },
        },
        "column"
      );
    },
  },
};
</script>
